import { Button, Text } from '@/components/primitives';
import { subtleActionButtonClass, subheaderButtonClass } from '@/components/primitives/Button/button.css';
import { DebugDetails } from './DebugDetails';
import { ErrorScreenLayout } from './ErrorScreenLayout';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { isProduction } from '@/lib/env';
import { vars } from '@/theme';
import { testIds } from '@/test/testIds';
import { dialogMessages } from '@/constants';

export interface FailureScreenProps {
  /** Trace id for this failure — shown to the user and correlatable to telemetry. */
  traceId: string;
  /** Copy-pasteable error report (buildErrorReport output). Shown only outside production. */
  report?: string;
  /** Reload the route — the safe universal retry. May be async. */
  onTryAgain: () => void | Promise<void>;
  /**
   * Navigate to a known-good place. May be async. Omit it for a person with no
   * session (see `offersDashboardExit`): the button is then not rendered.
   */
  onDashboard?: () => void | Promise<void>;
}

const ids = testIds.components.failureScreen;

/**
 * Full-page screen for the "we don't know what happened" class of error —
 * render crashes and 5xx. It is NOT a modal: a modal leaves a possibly-broken
 * screen behind it, and every exit here must lead somewhere known-good.
 */
export function FailureScreen({ traceId, report, onTryAgain, onDashboard }: FailureScreenProps) {
  const { copy, copied } = useCopyToClipboard();
  const showDebug = !isProduction() && !!report;

  return (
    <ErrorScreenLayout icon="!" iconBgColor={vars.color.danger.tint} testId={ids.root}>
      <Text variant="cardTitle" style={{ fontWeight: 600, marginBottom: 24, lineHeight: 1.5 }}>
        {dialogMessages.error.failureMessage}
      </Text>

      {/* Exits are safe-only: reload, or go somewhere known-good. No bare dismiss. */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        {onDashboard && (
          <Button
            onClick={onDashboard}
            data-testid={ids.dashboard}
            className={subtleActionButtonClass}
            style={{ borderRadius: 0, paddingLeft: 20, paddingRight: 20 }}
          >
            {dialogMessages.error.failureDashboard}
          </Button>
        )}
        <Button
          onClick={onTryAgain}
          data-testid={ids.tryAgain}
          className={subheaderButtonClass}
          style={{ height: 32, borderRadius: 0, paddingLeft: 20, paddingRight: 20, fontSize: 13 }}
        >
          {dialogMessages.error.failureTryAgain}
        </Button>
      </div>

      {showDebug && <DebugDetails report={report} />}

      {/* Trace id — pinned at the bottom, first-class and copyable. */}
      <div
        data-testid={ids.traceId}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 24,
          padding: '6px 12px',
          backgroundColor: vars.color.surface.muted,
          border: `1px solid ${vars.color.surface.border}`,
          borderRadius: 0,
        }}
      >
        <Text style={{ fontSize: 12, color: vars.color.neutral.base }}>
          {dialogMessages.error.traceIdLabel}:
        </Text>
        <Text style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 500 }}>
          {traceId}
        </Text>
        <button
          type="button"
          onClick={() => copy(traceId)}
          data-testid={ids.copyTrace}
          style={{
            padding: '2px 8px',
            fontSize: 11,
            fontFamily: 'inherit',
            border: `1px solid ${vars.color.surface.border}`,
            borderRadius: 0,
            background: copied ? '#e8f5e9' : vars.color.fixed.white,
            color: copied ? '#2e7d32' : vars.color.neutral.base,
            cursor: 'pointer',
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </ErrorScreenLayout>
  );
}
